import React, { useEffect, useMemo, useState } from "react";
import {
  Box, Paper, Stack, Typography, IconButton, Tooltip,
  TextField, MenuItem, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, TablePagination, Snackbar, CircularProgress,
  Button, Dialog, DialogTitle, DialogContent, DialogActions, FormControlLabel, Checkbox
} from "@mui/material";
import { Refresh, Download, Delete, UploadFile } from "@mui/icons-material";
import axios from "axios";
import * as XLSX from "xlsx";

export default function DocumentosRegistroAdmin({ colegiadoDestino = null }) {
  const API = process.env.REACT_APP_API_URL;
  const token = localStorage.getItem("token");
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const VISTAS = [
    { value: "ENTRADA", label: "Tabla de ENTRADA" },
    { value: "SALIDA", label: "Tabla de SALIDA" },
    { value: "AMBAS", label: "Ambas" },
  ];
  const REGISTROS = [
    { value: "ENTRADA", label: "Asignar a ENTRADA" },
    { value: "SALIDA", label: "Asignar a SALIDA" },
    { value: "NINGUNO", label: "No asignar" },
  ];
  const fmtFecha = (iso) => (iso ? new Date(iso).toLocaleString() : "-");

  const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
  const notify = (msg, sev = "success") => setSnack({ open: true, msg, sev });

  // filtros
  const [vista, setVista] = useState("ENTRADA");
  const [colegiadoIdFiltro, setColegiadoIdFiltro] = useState("");

  // paginación
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRpp] = useState(10);
  const [total, setTotal] = useState(0);

  // data
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);

  // export
  const [exporting, setExporting] = useState(false);

  // colegiado autenticado (si no se pasa prop)
  const [myColegiadoId, setMyColegiadoId] = useState(null);

  // subida
  const [openUpload, setOpenUpload] = useState(false);
  const [upForm, setUpForm] = useState({
    files: [],
    tipo_registro: "NINGUNO",
    autonumerar: true // (visible para futuro; ahora no afecta)
  });

  // obtener colegiado del usuario si no llega colegiadoDestino
  useEffect(() => {
    if (colegiadoDestino) return;
    async function fetchDetalle() {
      try {
        const { data } = await axios.get(`${API}/colegiado/detalle`, { headers: authHeaders });
        const codigo = data?.colegiado_codigo ?? data?.codigo ?? data?.colegiadoCodigo;
        setMyColegiadoId(codigo);
      } catch (e) {
        console.error(e);
        notify("No se pudo obtener tu detalle de colegiado.", "error");
      }
    }
    fetchDetalle();
  }, [API, authHeaders, colegiadoDestino]);

  // cargar listado paginado
  async function loadData() {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, size: rowsPerPage, vista });
      if (colegiadoIdFiltro) params.set("colegiado_id", colegiadoIdFiltro);

      const { data } = await axios.get(`${API}/api/documentos/registro?${params.toString()}`, {
        headers: authHeaders
      });

      setRows(data?.content ?? []);
      setTotal(data?.totalElements ?? data?.total_elements ?? 0);
    } catch (e) {
      console.error(e);
      notify("No se pudo cargar el listado.", "error");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadData();
    // eslint-disable-next-line
  }, [page, rowsPerPage, vista]);

  async function handleDescargar(id) {
    try {
      const res = await axios.get(`${API}/api/documentos/descargar/${id}`, {
        headers: authHeaders,
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: res.headers["content-type"] });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const cd = res.headers["content-disposition"] || "";
      const match = cd.match(/filename="?(.+?)"?$/i);
      a.href = url;
      a.download = match?.[1] || `documento_${id}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error(e);
      notify("No se pudo descargar el archivo.", "error");
    }
  }

  async function handleEliminar(id) {
    if (!window.confirm("¿Eliminar este documento?")) return;
    try {
      await axios.delete(`${API}/api/documentos/${id}`, { headers: authHeaders });
      notify("Documento eliminado.");
      loadData();
    } catch (e) {
      console.error(e);
      notify("No se pudo eliminar el documento.", "error");
    }
  }

  const targetColegiado = colegiadoDestino ?? myColegiadoId;
  const canSubmitUpload = Boolean(targetColegiado) && upForm.files.length > 0;

  async function subirDocumentos() {
    if (!canSubmitUpload) return;

    const tipo = upForm.tipo_registro;

    try {
      // 1) subir archivos
      const nuevosIds = [];
      for (const f of upForm.files) {
        const fd = new FormData();
        fd.append("file", f);
        fd.append("colegiadoId", targetColegiado);

        const { data: doc } = await axios.post(`${API}/api/documentos/admin/subir`, fd, {
          headers: authHeaders,
          transformRequest: d => d
        });
        if (doc?.id != null) nuevosIds.push(doc.id);
      }

      // 2) crear y asignar ENTRADA/SALIDA por cada documento
      if (tipo !== "NINGUNO" && nuevosIds.length) {
        for (const id of nuevosIds) {
          const payload = (tipo === "ENTRADA")
            ? { id, crearEntrada: true }
            : { id, crearSalida: true };

          await axios.put(
            `${API}/api/documentos/admin/actualizar-registro`,
            payload,
            { headers: { ...authHeaders, "Content-Type": "application/json" } }
          );
        }
      }

      notify("Documentos subidos correctamente.");
      setOpenUpload(false);
      setUpForm({ files: [], tipo_registro: "NINGUNO", autonumerar: true });
      setPage(0);
      await loadData();
    } catch (e) {
      console.error(e);
      notify("Error al subir/asignar documentos.", "error");
    }
  }

  // Exportar Excel (todas las páginas con filtros)
  async function handleExport() {
    try {
      setExporting(true);
      const SIZE = 1000; // tamaño grande para exportar
      let p = 0;
      let totalElems = Infinity;
      const acumulado = [];

      while (acumulado.length < totalElems) {
        const params = new URLSearchParams({ page: p, size: SIZE, vista });
        if (colegiadoIdFiltro) params.set("colegiado_id", colegiadoIdFiltro);

        const { data } = await axios.get(`${API}/api/documentos/registro?${params.toString()}`, {
          headers: authHeaders
        });

        const content = data?.content ?? [];
        totalElems = data?.totalElements ?? data?.total_elements ?? content.length;
        if (!content.length) break;

        acumulado.push(...content);
        if (content.length < SIZE) break; // última página
        p++;
        if (p > 5000) break; // guardaespaldas
      }

      const rowsXls = acumulado.map(r => ({
        ID: r.id,
        Colegiado: r.colegiadoCodigo ?? r.colegiado_codigo ?? "",
        "Nombre archivo": r.nombreArchivo ?? r.nombre_archivo ?? "",
        "Tipo contenido": r.tipoContenido ?? r.tipo_contenido ?? "",
        "Fecha subida": fmtFecha(r.fechaSubida ?? r.fecha_subida),
        "Reg. Entrada": r.registroEntradaId ?? r.registro_entrada_id ?? "",
        "Reg. Salida": r.registroSalidaId ?? r.registro_salida_id ?? "",
        Ruta: r.rutaArchivo ?? r.ruta_archivo ?? ""
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(rowsXls);
      XLSX.utils.book_append_sheet(wb, ws, "Documentos");

      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
      const fname = `documentos_${vista.toLowerCase()}_${colegiadoIdFiltro || "todos"}_${stamp}.xlsx`;
      XLSX.writeFile(wb, fname);

      notify(`Exportado ${rowsXls.length} registro(s).`);
    } catch (e) {
      console.error(e);
      notify("No se pudo exportar a Excel.", "error");
    } finally {
      setExporting(false);
    }
  }

  return (
    <Box p={2}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5">Registro ENTRADA/SALIDA</Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            startIcon={<UploadFile />}
            variant="contained"
            onClick={() => setOpenUpload(true)}
            disabled={!targetColegiado}
          >
            Subir documentos
          </Button>

          {/* Exportar */}
          <Button
            startIcon={<Download />}
            variant="outlined"
            onClick={handleExport}
            disabled={exporting}
          >
            {exporting ? "Exportando..." : "Exportar Excel"}
          </Button>

          <TextField
            select
            size="small"
            label="Vista"
            value={vista}
            onChange={(e) => { setVista(e.target.value); setPage(0); }}
            sx={{ minWidth: 210 }}
          >
            {VISTAS.map(v => <MenuItem key={v.value} value={v.value}>{v.label}</MenuItem>)}
          </TextField>
          <TextField
            size="small"
            type="number"
            label="Colegiado (opcional)"
            value={colegiadoIdFiltro}
            onChange={(e) => setColegiadoIdFiltro(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setPage(0); loadData(); } }}
            sx={{ width: 180 }}
          />
          <Tooltip title="Recargar">
            <IconButton onClick={() => { setPage(0); loadData(); }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Coleg.</TableCell> {/* <- corregido */}
                <TableCell>Nombre archivo</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>F. subida</TableCell>
                <TableCell align="right">Reg. Entrada</TableCell>
                <TableCell align="right">Reg. Salida</TableCell>
                <TableCell align="center">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    <CircularProgress size={20} />
                  </TableCell>
                </TableRow>
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">Sin resultados</TableCell>
                </TableRow>
              ) : (
                rows.map((r) => (
                  <TableRow key={r.id}>
                    <TableCell>{r.id}</TableCell>
                    <TableCell>{r.colegiado_codigo ?? r.colegiadoCodigo ?? "-"}</TableCell>
                    <TableCell>{r.nombre_archivo ?? r.nombreArchivo}</TableCell>
                    <TableCell>{r.tipo_contenido ?? r.tipoContenido}</TableCell>
                    <TableCell>{fmtFecha(r.fecha_subida ?? r.fechaSubida)}</TableCell>
                    <TableCell align="right">{r.registro_entrada_id ?? r.registroEntradaId ?? "-"}</TableCell>
                    <TableCell align="right">{r.registro_salida_id ?? r.registroSalidaId ?? "-"}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Descargar">
                        <IconButton onClick={() => handleDescargar(r.id)} size="small">
                          <Download fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Eliminar">
                        <IconButton onClick={() => handleEliminar(r.id)} size="small" color="error">
                          <Delete fontSize="inherit" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRpp(parseInt(e.target.value, 10)); setPage(0); }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Paper>

      <Snackbar
        open={snack.open}
        autoHideDuration={3000}
        onClose={() => setSnack(s => ({ ...s, open: false }))}
        message={snack.msg}
      />

      {/* --- DLG SUBIR --- */}
      <Dialog open={openUpload} onClose={() => setOpenUpload(false)} fullWidth maxWidth="sm">
        <DialogTitle>Subir documentos</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Se subirá a la ficha del colegiado #{targetColegiado ?? "..."}
            </Typography>
            <TextField
              select
              label="Tipo de registro"
              value={upForm.tipo_registro}
              onChange={e => setUpForm(f => ({ ...f, tipo_registro: e.target.value }))}
            >
              {REGISTROS.map(r => <MenuItem key={r.value} value={r.value}>{r.label}</MenuItem>)}
            </TextField>
            <FormControlLabel
              control={
                <Checkbox
                  checked={upForm.autonumerar}
                  onChange={e => setUpForm(f => ({ ...f, autonumerar: e.target.checked }))}
                  disabled={upForm.tipo_registro === "NINGUNO"}
                />
              }
              label="Autonumerar consecutivo"
            />
            <Button variant="outlined" component="label" startIcon={<UploadFile />}>
              Seleccionar archivos
              <input
                hidden
                type="file"
                multiple
                onChange={e => {
                  const fs = Array.from(e.target.files || []);
                  setUpForm(f => ({ ...f, files: fs }));
                }}
              />
            </Button>
            <Typography variant="body2" color="text.secondary">
              {upForm.files.length ? `${upForm.files.length} archivo(s) seleccionados` : "Ningún archivo seleccionado"}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenUpload(false)}>Cancelar</Button>
          <Button variant="contained" disabled={!canSubmitUpload} onClick={subirDocumentos}>
            Subir
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
